import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiPlus, FiX, FiEdit2, FiTrash2, FiImage } from 'react-icons/fi';
import { toast } from 'react-hot-toast';
import { api } from '../../../../api/api';
import StatusConfirmationModal from '../../../components/StatusConfirmationModal';

const LoginImageCard = ({ image, onEdit, onDelete }) => {
  const [imageError, setImageError] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow"
    >
      <div className="aspect-[16/9] overflow-hidden bg-gray-100">
        {!imageError ? (
          <img
            src={image.image}
            alt={image.title}
            className="w-full h-full object-cover"
            onError={() => {
              console.log('Failed to load image:', image.image);
              setImageError(true);
            }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <div className="text-gray-400 flex flex-col items-center">
              <FiImage className="w-8 h-8 mb-2" />
              <span className="text-sm">Image not available</span>
            </div>
          </div>
        )}
      </div>
      <div className="p-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">{image.title}</h3>
          <div className="flex items-center space-x-2">
            <button
              onClick={onEdit}
              className="p-2 text-gray-600 hover:bg-gray-50 rounded-full"
            >
              <FiEdit2 className="w-4 h-4" />
            </button>
            <button
              onClick={onDelete}
              className="p-2 text-red-600 hover:bg-red-50 rounded-full"
            >
              <FiTrash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const LoginImageModal = ({ image = null, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    title: '',
    image: null,
  });

  useEffect(() => {
    if (image) {
      setFormData({
        title: image.title,
        image: null,
      });
    }
  }, [image]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || (!image && !formData.image)) {
      toast.error('Please fill in all required fields');
      return;
    }

    const data = new FormData();
    data.append('title', formData.title);
    if (formData.image) {
      data.append('image', formData.image);
    }

    await onSave(data);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl w-full max-w-md">
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-semibold">
            {image ? 'Edit Login Image' : 'Add Login Image'}
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <FiX className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Title *
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Image {!image && '*'}
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setFormData({ ...formData, image: e.target.files[0] })}
              className="w-full"
              required={!image}
            />
          </div>
          <div className="flex justify-end gap-4 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 border rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
            >
              {image ? 'Update' : 'Add'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const LoginTab = () => {
  const [loginImages, setLoginImages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingImage, setEditingImage] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [imageToDelete, setImageToDelete] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);

  useEffect(() => {
    fetchLoginImages();
  }, []);

  const fetchLoginImages = async () => {
    try {
      setIsLoading(true);
      const data = await api.getLoginImages();
      setLoginImages(data);
    } catch (err) {
      setError('Failed to fetch login images');
      toast.error('Failed to load login images');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteImage = (image) => {
    setImageToDelete(image);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      const loadingToast = toast.loading('Deleting image...');
      await api.deleteLoginImage(imageToDelete.id);
      setLoginImages(prev => prev.filter(image => image.id !== imageToDelete.id));
      toast.dismiss(loadingToast);
      toast.success('Login image deleted successfully');
    } catch (error) {
      toast.error('Failed to delete login image');
      console.error('Error deleting login image:', error);
    } finally {
      setShowDeleteModal(false);
      setImageToDelete(null);
    }
  };

  const handleEditImage = async (formData) => {
    try {
      await api.updateLoginImage(editingImage.id, formData);
      await fetchLoginImages();
      setEditingImage(null);
      toast.success('Login image updated successfully');
    } catch (error) {
      toast.error(error.message || 'Failed to update login image');
    }
  };

  const handleAddImage = async (formData) => {
    try {
      await api.createLoginImage(formData);
      await fetchLoginImages();
      setShowAddModal(false);
      toast.success('Login image added successfully');
    } catch (error) {
      toast.error(error.message || 'Failed to add login image');
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center text-red-600 p-4">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl p-4 md:p-6 shadow-sm">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-semibold">Login Images</h3>
          <button
            onClick={() => setShowAddModal(true)}
            className="px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 flex items-center gap-2"
          >
            <FiPlus className="h-5 w-5" />
            Add Image
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {loginImages.map((image) => (
            <LoginImageCard
              key={image.id}
              image={image}
              onEdit={() => setEditingImage(image)}
              onDelete={() => handleDeleteImage(image)}
            />
          ))}
        </div>
      </div>

      {editingImage && (
        <LoginImageModal
          image={editingImage}
          onClose={() => setEditingImage(null)}
          onSave={handleEditImage}
        />
      )}

      {showAddModal && (
        <LoginImageModal
          onClose={() => setShowAddModal(false)}
          onSave={handleAddImage}
        />
      )}

      <StatusConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setImageToDelete(null);
        }}
        onConfirm={confirmDelete}
        itemName="Login Image"
        currentStatus={true}
        message="Are you sure you want to delete this login image? This action cannot be undone."
      />
    </div>
  );
};

export default LoginTab;
